import type { ChangeEvent } from "react";

import { AIBorderlessButton } from "@similarweb/ai-kit";
import { Tooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import type { PromptSchema } from "app/pages/activation/components/SetupModal/CompetitorsStep/types";
import { PromptSource } from "app/pages/activation/components/SetupModal/CompetitorsStep/types";

import { MAX_AI_INPUT_LENGTH } from "./constants";
import * as SC from "./styles";
import { AI_BUTTON_TEXT, AI_BUTTON_TOOLTIP, AI_INPUT_PLACEHOLDER } from "./texts";

type AIInputSchema = {
    promptText: string;
    isDisabled: boolean;
    isLoading: boolean;
    onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    handleSetPrompt: (prompt: PromptSchema) => void;
    onFocus: () => void;
    onBlur: () => void;
};

export const AIInput = ({
    onChange,
    handleSetPrompt,
    promptText,
    isLoading,
    isDisabled,
    onFocus,
    onBlur,
}: AIInputSchema) => {
    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        onChange(e);
    };

    const onSetPrompt = () => {
        handleSetPrompt({ prompt: promptText, source: PromptSource.Input });

        setTimeout(() => {
            onBlur();
        }, 200);
    };

    const onEnter = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        e.stopPropagation();

        if (!e.shiftKey && e.key === "Enter") {
            e.preventDefault();

            onSetPrompt();
        }
    };

    const hasTooltip = !promptText.length && !isLoading;

    return (
        <SC.InputWrapper>
            <SC.StyledAIcon iconName="spark-ai" />
            <SC.StyledAIInput
                id="refine-suggestion"
                onChange={handleChange}
                value={promptText}
                placeholder={translate(AI_INPUT_PLACEHOLDER)}
                onKeyDown={onEnter}
                onFocus={onFocus}
                onBlur={onBlur}
                disabled={isDisabled}
                maxLength={MAX_AI_INPUT_LENGTH}
            />

            <SC.ButtonWrapper>
                <Tooltip
                    enabled={hasTooltip}
                    content={<SC.TooltipContent>{translate(AI_BUTTON_TOOLTIP)}</SC.TooltipContent>}
                >
                    <SC.RefineButtonWrapper>
                        <SC.StyledAIIcon
                            iconName="sparkles-gradient"
                            size="xs"
                            isDisabled={isDisabled}
                        />
                        <AIBorderlessButton
                            label={translate(AI_BUTTON_TEXT)}
                            onClick={onSetPrompt}
                            disabled={isDisabled}
                        />
                    </SC.RefineButtonWrapper>
                </Tooltip>
            </SC.ButtonWrapper>
        </SC.InputWrapper>
    );
};
